import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { toast } from "../hooks/use-toast";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageOrderDialog = ({ open, onOpenChange, packageName = "" }) => {
  const { t, lang } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setSubmitted(false);
  }, [open]);

  const title = packageName ? `${t("cta.leaveRequest", "Залишити заявку")} — ${packageName}` : t("form.title", "Запис на консультацію");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: t("form.required", "Заповніть обов'язкові поля"), description: t("form.requiredDesc", "Потрібні ім'я та телефон"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        program: packageName,
        message: packageName ? `Заявка на пакет: ${packageName}` : "",
        lang,
        source: window.location.pathname + (packageName ? ` · ${packageName}` : "")
      });
      setSubmitted(true);
      toast({ title: t("form.sent", "Заявку відправлено"), description: t("form.sentDesc", "Менеджер зв'яжеться найближчим часом") });
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      toast({ title: "Error", description: t("cta.askExpert", "Try again"), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] rounded-[1.5rem]" data-testid="package-order-dialog">
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold w-fit mb-2 uppercase tracking-widest">
                <Sparkles className="w-3 h-3" /> {t("form.badge", "Безкоштовно · 30 хвилин")}
              </div>
              <DialogTitle className="font-display text-2xl uppercase" data-testid="package-order-title">{title}</DialogTitle>
              <DialogDescription>{t("form.desc", "Менеджер зв'яжеться протягом 30 хвилин")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2" data-testid="package-order-form">
              <div>
                <Label htmlFor="po-name" className="text-xs">{t("form.name", "Ім'я")} *</Label>
                <Input id="po-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder={t("form.namePh", "Олександр")} className="mt-1" data-testid="package-order-name" />
              </div>
              <div>
                <Label htmlFor="po-phone" className="text-xs">{t("form.phone", "Телефон")} *</Label>
                <Input id="po-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+380 ..." className="mt-1" data-testid="package-order-phone" />
              </div>
              <div>
                <Label htmlFor="po-email" className="text-xs">{t("form.email", "Email")}</Label>
                <Input id="po-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@email.com" className="mt-1" data-testid="package-order-email" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full btn-amber rounded-full h-12 font-semibold mt-2" data-testid="package-order-submit">
                {submitting ? "..." : t("cta.sendRequest", "Надіслати заявку")}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">{t("form.privacy", "Натискаючи кнопку, ви погоджуєтесь з політикою конфіденційності")}</p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center" data-testid="package-order-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <DialogTitle className="font-display text-2xl uppercase">{t("form.accepted", "Заявку прийнято")}</DialogTitle>
            <DialogDescription className="mt-2">{t("form.acceptedDesc", "Наш менеджер зв'яжеться найближчим часом.")}</DialogDescription>
            <Button onClick={() => onOpenChange(false)} className="mt-6 btn-amber rounded-full h-11 px-6 font-semibold">
              {t("form.great", "Чудово")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PackageOrderDialog;
